import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import Button from '@mui/material/Button';
import { List, ListItem, ListItemIcon, ListItemText, Modal, Typography } from '@mui/material';
import { Cancel, CheckCircle, Close, MoreHoriz } from '@mui/icons-material';
import { GridCheckCircleIcon } from '@mui/x-data-grid';

interface TablePaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </Box>
    );
}

// New row structure
interface RowData {
    plan: string;
    description: string;
    details: string,
    benefits: string[],
    price: number;
    status: string;
    startedAt: string;
    lastBilledAt: string;
    nextBillingDate: string | null;
    cardDetails: {
        cardNumber: string,
        cardExpiry: string,
    }
}

// Sample rows

type CompProps = {
    data: RowData[]
}


export default function CustomPaginationActionsTable({ data }: CompProps) {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [openModal, setOpenModal] = React.useState({
        open: false,
        data: {
            description: "",
            details: "",
            benefits: [""],
        },
    });

    const handleModal = (row : RowData) => {
        setOpenModal({ ...openModal, open: true, data: { description: row.description, benefits: row.benefits, details: row.details} })
    }
    const onClose = () => setOpenModal({...openModal, open: !open})

    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (

        <>
            <Modal
                open={openModal.open}
                onClose={onClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                sx={{ maxWidth: "600px", mx: "auto", display: "flex", justifyContent: "center", alignItems: "center", overflow: "hidden", }}

            >
                <Box sx={{ bgcolor: "whitesmoke", borderRadius: "5px" }}>
                    <Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between", borderBottom: "1px solid rgba(160, 160, 160, 0.47)", p: "20px", }}>
                        <Typography sx={{ fontWeight: "bold", fontSize: "20px", mb: "10px" }}>{openModal.data?.description}</Typography>
                        <Close sx={{ fontSize: "25px", cursor: "pointer" }} onClick={onClose} />
                    </Box>

                    <Box sx={{ p: "20px", }}>
                        <Typography sx={{ my: "20px" }}>
                            {openModal.data?.details}
                        </Typography>

                        <Typography sx={{ fontWeight: "bold" }}>Benefits</Typography>

                        {openModal.data?.benefits?.map((list, index) => (
                            <List sx={{ mt: 1 }} key={index}>
                                <ListItem disablePadding>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <GridCheckCircleIcon
                                            className="icons"
                                            sx={{ color: "#2bd40f" }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText>{list}</ListItemText>
                                </ListItem>
                            </List>
                        ))}

                    </Box>
                </Box>
            </Modal>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 1200 }} aria-label="custom pagination table">
                    <TableBody>
                        <TableRow>
                            <TableCell><strong>Plan</strong></TableCell>
                            <TableCell><strong>Description & Benefits</strong></TableCell>
                            <TableCell><strong>Price</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Started At</strong></TableCell>
                            <TableCell><strong>Last Billed At</strong></TableCell>
                            <TableCell><strong>Next Billing Date</strong></TableCell>
                            <TableCell><strong>Card Details</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                        {(rowsPerPage > 0
                            ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            : data
                        ).map((row, index) => (
                            <TableRow key={index}>
                                <TableCell>{row.plan}</TableCell>
                                <TableCell onClick={() => handleModal(row)} sx={{
                                    cursor: "pointer",
                                    color: "#0000EE",
                                    textDecoration: "underline",
                                    "&:hover .actions": {
                                        display: "inline-flex"
                                    }
                                }}>
                                    <Typography noWrap sx={{ maxWidth: 150, fontSize: "14px", }}>
                                        {row.description}....
                                    </Typography>
                                </TableCell>
                                <TableCell>${row.price}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                        {row.status === "Active" ? <>
                                            <CheckCircle color='success' sx={{ width: "20px" }} /> {row.status}</> : <>
                                            <Cancel color='warning' sx={{ width: "20px" }} /> {row.status}
                                        </>}
                                    </Box>
                                </TableCell>
                                <TableCell>{row.startedAt}</TableCell>
                                <TableCell>{row.lastBilledAt}</TableCell>
                                <TableCell>{row.nextBillingDate ? row.nextBillingDate : "null"}</TableCell>
                                <TableCell>{row.cardDetails.cardNumber}
                                    <Box sx={{}}>
                                        <Typography sx={{ fontSize: "12px", }}>
                                            ( Expires on {row.cardDetails.cardExpiry} )
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {row.status === "Active" ? <MoreHoriz sx={{ cursor: "pointer" }} /> : <Button sx={{ fontSize: "14px" }}>
                                        Renew
                                    </Button>}
                                </TableCell>
                            </TableRow>
                        ))}
                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={9} />
                            </TableRow>
                        )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                                colSpan={9}
                                count={data.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                slotProps={{
                                    select: {
                                        inputProps: {
                                            'aria-label': 'rows per page',
                                        },
                                        native: true,
                                    },
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </>
    );
}
